import React, {
    forwardRef,
    useContext,
    useEffect,
    useRef,
    useState,
    useCallback,
} from 'react'
import Tree from 'react-d3-tree'
import LeavesContext from '../../contexts/LeavesContext'
import DetailsPanelContext from '../../contexts/DetailsPanelContext'
import styled from 'styled-components'
import { PanZoom } from 'react-easy-panzoom'
import useModal from '../../hooks/useModal'
import ProfileModal from './ProfileModal'
import RemoveLeaf from './DetailsPanel/RemoveLeaf'
import useBranchHierarchy from '../../hooks/useBranchHierarchy'
import useTreeMembers from '../../hooks/useTreeMembers'
import ViewPreferenceContext from '../../contexts/ViewPreferenceContext'
import useD3TreeData from '../../hooks/useD3TreeData'
import D3TreeLeafNode from './D3TreeLeafNode'
import customPathFunc from '../../utils/customPathFunc'

const StyledBranch = styled.div`
    display: inline-flex;
    flex-direction: column;
    align-items: center;
    margin: 0 8px;

    &:hover {
        cursor: grab;
    }
`

const StyledDescendantsContainer = styled.div`
    display: flex;
    flex-direction: row;
`

const StyledPartnersContainer = styled.div`
    display: flex;
    flex-direction: row;

    &:hover + .descendants {
        .leaf_connections {
            z-index: -1;
            path {
                transition: all 0.1s ease-in-out;
                stroke: var(--color_outline);
                stroke-width: 4;
            }
        }
    }
`

const StyledTogetherContainer = styled.div`
    display: flex;
`

const StyledApartLeaves = styled.div`
    display: flex;
`

const StyledPanZoom = styled(PanZoom)`
    // display: inline-flex;
    // position: relative;
    // left: 50%;
    // transform: translateX(-50%);
    // padding: 40px;
    // z-index: 0;
    // transition: padding-left var(--speed) ease-out;

    &:focus {
        outline: none;
    }
`

const StyledTreeContainer = styled.div`
    width: 100%;
    height: 100vh;
    min-height: 600px;

    .rd3t-tree-container {
        width: 100%;
        height: 100%;
        background: transparent;
    }

    .rd3t-link {
        stroke: var(--color_border_300);
        stroke-width: 2;
        fill: none;
        z-index: -1;
    }

    .rd3t-node {
        cursor: pointer;
    }
`

export const Branch = forwardRef((props, ref) => {
    const { leaves } = useContext(LeavesContext)
    const { panelState, setPanelState } = useContext(DetailsPanelContext)
    const [toggleProfileModal, isProfileModalShowing] = useModal(false)
    const [toggleDeleteModal, isDeleteModalShowing] = useModal(false)
    const { hierarchy } = useBranchHierarchy(leaves)
    const { treeMembers, getClaimedBy } = useTreeMembers()
    const { viewPreference } = useContext(ViewPreferenceContext)
    const treeData = useD3TreeData(hierarchy)
    const treeContainerRef = useRef(null)

    // Handle node click
    const handleNodeClick = useCallback(
        (nodeData) => {
            const leafId =
                nodeData.attributes?.leafId || nodeData.attributes?.id
            const leafDoc = leaves.find((leaf) => leaf.id === leafId)
            const memberDoc =
                leafDoc?.claimed_by && getClaimedBy(leafDoc.claimed_by)

            setPanelState({
                ...panelState,
                leafId: leafId,
                leafDoc: leafDoc,
                linkedMemberDoc: memberDoc,
            })
        },
        [leaves, panelState, setPanelState, getClaimedBy]
    )

    // Handle node double click - open profile modal
    const handleNodeDoubleClick = useCallback(
        (nodeData) => {
            handleNodeClick(nodeData)
            toggleProfileModal()
        },
        [handleNodeClick, toggleProfileModal]
    )

    // Don't render if in table view or no data
    if (viewPreference.table || !treeData) {
        return null
    }

    return (
        <>
            {leaves.length !== 0 && treeData && (
                <StyledTreeContainer ref={treeContainerRef}>
                    <Tree
                        data={treeData}
                        orientation="vertical"
                        pathFunc={customPathFunc}
                        separation={{ siblings: 1.5, nonSiblings: 1.5 }}
                        translate={{ x: 400, y: 100 }}
                        scaleExtent={{ min: 0.35, max: 1.2 }}
                        zoomable={true}
                        draggable={true}
                        collapsible={false}
                        nodeSize={{ x: 200, y: 200 }}
                        renderCustomNodeElement={(rd3tProps) => (
                            <D3TreeLeafNode
                                {...rd3tProps}
                                onNodeClick={handleNodeClick}
                                onNodeDoubleClick={handleNodeDoubleClick}
                            />
                        )}
                    />
                </StyledTreeContainer>
            )}

            {isProfileModalShowing && (
                <ProfileModal
                    isMember={panelState?.linkedMemberDoc ? true : false}
                    isModalShowing={isProfileModalShowing}
                    hide={toggleProfileModal}
                />
            )}
            {isDeleteModalShowing && (
                <RemoveLeaf
                    isModalShowing={isDeleteModalShowing}
                    hide={toggleDeleteModal}
                />
            )}
        </>
    )
})

export default Branch
